
import {Component, Vue, Prop, Watch} from "vue-property-decorator";
import {formRules} from "@/utils/formRules";
export const CONNECTED_BOXES = "CONNECTED_BOXES";
export const FREE = "FREE";
export const B2B = "B2B";
export const AGENCY = "AGENCY";

@Component
export default class CompanySubscriptionForm extends Vue {

    @Prop({default: {}})
    public subscription: any;

    @Prop({default: {}})
    public company: any;

    @Prop({default: () => []})
    public relaysWithConnectedBoxes: any[];

    @Prop({default: () => []})
    public relaysWithMasterlocks: Array<{
      name: string,
      count: number,
    }>;

    private plans: any[] = [];
    private rules: object = formRules;
    private startDateMenu: boolean = false;
    private endDateMenu: boolean = false;
    private connectBoxesModal: boolean = false;

    @Watch("subscription.Plan")
    public handlerPlanChange(newPlan: any) {
        if (newPlan.type === "B2B") {
            // Automatically activate "more relays" option if plan authorized it
            this.subscription.details.options.additionalRelays.isActive = !!newPlan.details.canCreateMoreRelays;
            // Automatically activate "deposit in flow relays" option if plan authorized it
            this.subscription.details.options.dropKeyInFlowRelaysSingle.isActive = !!newPlan.details.canUseFlowRelays;
            // Automatically activate "manage stock" option if plan authorized it
            this.subscription.details.options.manageStock.isActive = !!newPlan.details.canManageStock;
            // Automatically activate "allow Booking" option if plan authorized it
            this.subscription.details.options.allowBooking.isActive = !!newPlan.details.canUseBooking;
            // Automatically activate "connected box" option if plan authorized it
            this.subscription.details.options.connectedBoxes.isActive = this.companyHasConnectedBoxes
                || !!newPlan.details.canUseConnectedBox;
            // Automatically activate "masterlocks" option if already created
            this.subscription.details.options.masterlocks.isActive = this.companyHasMasterlocks;
            // Set subscription maxSms to plan default maxSms value
            this.subscription.maxSms = newPlan.details.maxSms;
        }
        if (newPlan.name === "PREMIUM") {
            this.subscription.details.options.allowBooking.initValue = 0;
            this.subscription.details.options.manageStock.initValue = 0;
        } else {
            this.subscription.details.options.allowBooking.initValue = 50;
            this.subscription.details.options.manageStock.initValue = 50;
        }
    }

    @Watch("subscription.details.options.dropKeyInFlowRelaysSingle.isActive", { immediate: true })
    public handlerSingleKeyOption(newVal: boolean) {
        if (!newVal) {
            this.subscription.details.options.dropKeyInFlowRelaysPackage.isActive = false;
        }
    }

    private mounted() {
        this.getPlans();
        if (this.subscription.startDate) {
            this.subscription.startDate = new Date(this.subscription.startDate).toISOString().substr(0, 10);
        }
        if (this.subscription.endDate) {
            this.subscription.endDate = new Date(this.subscription.endDate).toISOString().substr(0, 10);
        }
        this.inputConnectedBoxesAndExtensions();
    }

    get lang(): string {
        return this.$root.$i18n.locale;
    }

    get classicOptions(): any[] {
        return Object.keys(this.subscription.details.options)
            .filter((option) => option !== "dropKeyInFlowRelaysPackage"
                && !this.tailorMadeOptions.includes(option),
            );
    }

    get activeOptions(): any[] {
        return this.classicOptions.filter((option) => this.subscription.details.options[option].isActive);
    }

    get singleKeyOption(): any {
        return this.subscription.details.options?.dropKeyInFlowRelaysSingle;
    }

    get companyHasConnectedBoxes(): boolean {
      // relay with box (created in CompanySubscription)
        return this.relaysWithConnectedBoxes.some((relay) => relay.boxes.length > 0);
    }

    get companyHasMasterlocks(): boolean {
        return !!this.relaysWithMasterlocks.length;
    }

    private forceOption(option: string): boolean {
        if (!["connectedBoxes", "masterlocks"].includes(option)) {
            return false;
        }
        const isActive = this.subscription.details.options[option].isActive;
        if (isActive && option === "connectedBoxes") {
          return this.subscription.Plan?.name === "CONNECTED_BOXES" || this.companyHasConnectedBoxes;
        }
        if (isActive && option === "masterlocks") {
          return this.companyHasMasterlocks;
        }
    }

    get isSubscriptionTailorMade(): boolean {
        return this.subscription?.Plan?.name === "TAILOR_MADE";
    }

    get tailorMadeOptions(): string[] {
        return ["additionalTenKeys", "additionalUser"];
    }

    private getPlans() {
        const query = {type: this.company.type};
        return this.$store.dispatch("admin/plans/read", {query})
        .then((res) => {
            this.plans = res.plans.sort((a, b) => a.monthlyPrice - b.monthlyPrice);
        });
    }

    get formattedPlans() {
        return this.plans
        .map((plan) => ({
            key: plan.id,
            label: plan.name !== CONNECTED_BOXES
                ? this.$t(`plan.${plan.name}.nameAndPrice`, {price: plan.monthlyPrice})
                : this.$t(`plan.${plan.name}.name`) + ` (${plan.details.connectedBoxPrice}€/${this.$t("plan." + plan.name + ".name")})`,
            plan: {...plan},
        }));
    }

    get planInitialPrice(): number {
        return this.subscription.paymentPeriodicity === "MONTHLY"
            ? this.subscription.Plan.monthlyPrice
            : this.subscription.Plan.yearlyPrice;
    }

    get paymentPeriodicityMultiplier(): number {
        return this.subscription.paymentPeriodicity === "MONTHLY" ? 1 : 12;
    }

    private isPlanNotFree = (plan: any) => plan.name && plan.name !== FREE;

    private inputConnectedBoxesAndExtensions() {
        if (!this.subscription.Plan.details) {
            return;
        }
        const boxPrice = this.subscription.Plan.details.connectedBoxPrice || 89;

        let price = 0;
        this.relaysWithConnectedBoxes.forEach((relay) => {
            const relatedBoxConnected = relay.boxes.filter((box) => box.ConnectedBox);
            price += boxPrice * relatedBoxConnected.length;
        });
        this.subscription.details.options.connectedBoxes.initValue = price;
    }
}
